import React, { FC, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import CreateCompany from './CreateCompany';
import Settings from './Settings';
import { ToastContainer } from 'react-toastify';
import StatusCheckWrapper from './misc/StatusCheckWrapper';
import ChatInterface from './chat/ChatInterface';
import ProDocView from './chat/ProDocView';
import ProDocList from './chat/ProDocList';
import DreamDaily from './chat/DreamDaily';
import MemoryArea from './chat/MemoryArea';
import { AgentProvider, useAgent } from '../contexts/AgentContext';
import { ReplyToProvider } from '../contexts/ReplyToContext';
import './chat/conversational-ui.scss';
import ConfirmAction from './ConfirmAction';
import UniPage from './chat/UniPage';
import BookingPage from './BookingPage';
import WebRTCVoiceChat from './WebRTCVoiceChat';
import HumanTasks from './chat/HumanTasks';
import SalesPlan from './SalesPlan';
import OrgTiers from './OrgTiers';
import { SalesInbox } from './chat/SalesInbox';
import TodoListPage from './chat/TodoListPage';
import AgentHomePage from './chat/AgentHomePage';
import TodayPage from './chat/TodayPage';
import FastChatButton from './common/FastChatButton';
import CallNotification from './common/CallNotification';
import TrialBanner from './common/TrialBanner';
import { OnboardingModal } from './onboarding/OnboardingModal';
import { PushNotificationModal } from './onboarding/PushNotificationModal';
import { MobileNavProvider, useMobileNav } from '../contexts/MobileNavContext';
import MobileNavigation from './chat/MobileNavigation';
import UseCaseEditor from './chat/UseCaseEditor';
import SuggestionArchetypeEditor from './chat/SuggestionArchetypeEditor';
import SuggestionSimulation from './chat/SuggestionSimulation';
import PlatformStats from './chat/PlatformStats';
import AiCallTester from './admin/AiCallTester';
import NotFoundPage from './NotFoundPage';

const LayoutMobileNavigation: FC = () => {
  const { isMobile, isMobileNavOpen, closeMobileNav } = useMobileNav();
  if (!isMobile) return null;
  return <MobileNavigation isOpen={isMobileNavOpen} onClose={closeMobileNav} />;
};

const ConversationalUIContent: FC = () => {
  const { loadAgents } = useAgent();
  const location = useLocation();

  const isVoiceChat = location.pathname === '/voice-chat';
  const isDreamDaily = location.pathname === '/dream-daily';
  const isHumanTasks = location.pathname === '/tasks';
  const isBaseRoute = location.pathname.indexOf('/docs') !== -1;

  // Detect standalone PWA mode and add CSS class (iOS doesn't always match display-mode: standalone)
  useEffect(() => {
    const isStandalone =
      (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      document.documentElement.classList.add('pwa-standalone');
    }
  }, []);

  // Load agents on component mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  return (
    <div className='conversational-outer'>
      <TrialBanner />
      <div
        className={`conversational-container ${isVoiceChat || isDreamDaily || isHumanTasks ? 'no-nav' : ''} ${isBaseRoute ? 'high-af' : ''}`}
      >
        {/* {!isVoiceChat && <NotificationPermissionBanner />} */}
        <div className={`conversational-content`}>
          <Routes>
            <Route path='/' element={<UniPage />} />
            <Route path='/todos' element={<TodoListPage />} />
            <Route path='/conversations' element={<ChatInterface />} />
            <Route path='/memory' element={<MemoryArea />} />
            <Route path='/docs' element={<ProDocList />} />
            <Route path='/docs/:proDocId' element={<ProDocView />} />
            <Route path='/dream-daily' element={<DreamDaily />} />
            <Route path='/tasks' element={<HumanTasks />} />
            <Route path='/sales-plan' element={<SalesPlan />} />
            <Route path='/sales-inbox' element={<SalesInbox />} />
            <Route path='/agent-home' element={<AgentHomePage />} />
            <Route path='/today' element={<TodayPage />} />
            <Route path='/booking/:key' element={<BookingPage />} />

            <Route
              path='/confirm/:confirmationCode'
              element={<ConfirmAction />}
            />
            <Route path='/create-company' element={<CreateCompany />} />
            <Route path='/settings' element={<Settings />} />
            <Route path='/settings/:setting' element={<Settings />} />
            <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
            <Route path='/org-tiers' element={<OrgTiers />} />
            <Route path='/use-cases' element={<UseCaseEditor />} />
            <Route
              path='/suggestion-archetypes'
              element={<SuggestionArchetypeEditor />}
            />
            <Route
              path='/suggestion-simulation'
              element={<SuggestionSimulation />}
            />
            <Route path='/platform-stats' element={<PlatformStats />} />
            <Route path='/ai-call-test' element={<AiCallTester />} />
            {/* Add any other routes that should be accessible in conversational mode */}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
        </div>
        <ToastContainer className='toast-container-wrapper' />
        <StatusCheckWrapper />
        {/* <FastChatButton /> */}
        <CallNotification />

        <PushNotificationModal />

        <LayoutMobileNavigation />

        <OnboardingModal
          modalKey='new_layout'
          title="We've made it easier to find what's important"
          imageSrc='/onboarding-modals/new-layout.png'
          primaryButtonText='Got it'
        >
          <p>
            We heard you tell us that finding campaigns and chats was hard work,
            so we've made them easier to find with a new, simpler navigation.
          </p>
          <p>
            Easily see what chats have updates, and get to them directly from
            the navigation bar. Start a new chat or campaign straight from the
            nav bar.
          </p>
        </OnboardingModal>
      </div>
    </div>
  );
};

const ConversationalUI: FC = () => {
  return (
    <AgentProvider>
      <ReplyToProvider>
        <MobileNavProvider>
          <ConversationalUIContent />
        </MobileNavProvider>
      </ReplyToProvider>
    </AgentProvider>
  );
};

export default ConversationalUI;
